import { ThreeCanvas } from "@remotion/three";
import { useCurrentFrame, useVideoConfig, delayRender, continueRender, cancelRender, staticFile } from "remotion";
import { useEffect, useMemo, useState } from "react";
import { BufferAttribute, BufferGeometry, DoubleSide } from "three";
import { createFunnel, createRail, type Triangles } from "./geometry";
import { marbleConfig } from "./config";

type MarbleState = {
  id: number;
  p: [number, number, number];
  q: [number, number, number, number];
  visible: boolean;
};
type Recording = {
  fps: number;
  frames: MarbleState[][];
};

const meshGeometry = (mesh: Triangles) => {
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(new Float32Array(mesh.vertices), 3));
  geometry.setIndex(mesh.indices);
  geometry.computeVertexNormals();
  return geometry;
};

const Course = () => {
  const funnel = useMemo(() => meshGeometry(createFunnel()), []);
  const rail = useMemo(() => meshGeometry(createRail()), []);
  useEffect(() => () => {
    funnel.dispose();
    rail.dispose();
  }, [funnel, rail]);
  return (
    <>
      <mesh geometry={funnel} castShadow receiveShadow>
        <meshStandardMaterial color="#f8edcf" side={DoubleSide} roughness={0.67} />
      </mesh>
      <mesh geometry={rail} castShadow receiveShadow>
        <meshStandardMaterial color="#f2dcad" side={DoubleSide} roughness={0.7} />
      </mesh>
      <mesh position={[0, 2.67, -1.16]} castShadow receiveShadow>
        <boxGeometry args={[3.4, 0.64, 0.24]} />
        <meshStandardMaterial color="#f2dcad" roughness={0.7} />
      </mesh>
    </>
  );
};

export const MarbleFunnelExperiment = () => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const [handle] = useState(() => delayRender("Loading precomputed Rapier recording"));
  const [recording, setRecording] = useState<Recording | null>(null);

  useEffect(() => {
    let active = true;
    fetch(staticFile("marble-recording.json"))
      .then((response) => {
        if (!response.ok) throw new Error("Generate physics first: npm run marble:physics");
        return response.json() as Promise<Recording>;
      })
      .then((value) => {
        if (!active) return;
        if (value.fps !== marbleConfig.fps ||
          value.frames.length !== marbleConfig.durationSeconds * marbleConfig.fps) {
          throw new Error("Marble recording does not match the composition");
        }
        setRecording(value);
        continueRender(handle);
      })
      .catch((error: unknown) => {
        if (active) cancelRender(error);
      });
    return () => { active = false; };
  }, [handle]);

  const marbles = recording?.frames[frame] ?? [];
  return (
    <ThreeCanvas
      width={width}
      height={height}
      orthographic
      camera={{ position: [6, 11, 12], zoom: width / 6.2, near: 0.1, far: 100 }}
      shadows={{ type: 2 }}
      gl={{ antialias: true, preserveDrawingBuffer: true }}
      style={{ background: "#fff9ed" }}
    >
      <color attach="background" args={["#fff9ed"]} />
      <ambientLight intensity={1.45} />
      <directionalLight
        position={[-3, 9, 6]}
        intensity={1.65}
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-camera-left={-7}
        shadow-camera-right={7}
        shadow-camera-top={9}
        shadow-camera-bottom={-9}
        shadow-bias={-0.00015}
        shadow-radius={7}
      />
      <group position={[-0.7, -2.35, 0]}>
        <Course />
        {marbles.filter((ball) => ball.visible).map((ball) => (
          <mesh
            key={ball.id}
            position={ball.p}
            quaternion={ball.q}
            castShadow
            receiveShadow
          >
            <sphereGeometry args={[marbleConfig.ballRadius, 24, 16]} />
            <meshPhysicalMaterial
              color={marbleConfig.colors[ball.id % marbleConfig.colors.length]}
              roughness={0.27}
              metalness={0.08}
              clearcoat={0.45}
              clearcoatRoughness={0.24}
            />
          </mesh>
        ))}
      </group>
    </ThreeCanvas>
  );
};

